import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { logger } from '@adornis/base/logging.js';
import { Int, ID } from '@adornis/baseql/baseqlTypes.js';
import { NotAuthorizedError } from '@adornis/baseql/errors/NotAuthorizedError.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '../server/scoped-collection.js';
import { context, serverContext } from '@adornis/baseql/server/context.js';
import { subscribePaginated, subscribePaginatedAggregation } from '@adornis/baseql/server/resolver/mongo.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import {
  delay,
  getCurrentUserServer,
  getCurrentUserServerOrFail,
  loadRawEntityDataByID,
  loadRawEntityDataByIDOrFail,
  loadRawEntityDataByIDs,
  loadRawEntityDataByQuery,
  mongoUpdateOne,
  saveEntityData,
  type ServerCachedUser,
} from '@adornis/bh-util/util.js';
import { ZoomUserAccessToken } from '@adornis/bh-video-conferencing/api/zoom.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { MailTemplate } from '@adornis/mails/db/mail-template.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { prepareMessage } from '@adornis/mails/server/communication.js';
import { createStripeCustomer } from '@adornis/payments/server/customer.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { emailRegexCheck } from '@adornis/users/db/validators.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { assignRoleToUser } from '@adornis/users/server/roles.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { createHash } from 'node:crypto';
import { concatMap, from, merge, of, switchMap, map } from 'rxjs';
import { getDbPluginForScope } from '../db-plugins.js';
import { ActionTokenData } from '../entities/action.js';
import { BHFilter } from '../entities/bh-filter.js';
import { DynamicAdornisRoleToContextsWrapper } from '../entities/DynamicAdornisRoleToContextsWrapper.js';
import { Einzelberatung } from '../entities/einzelberatung.js';
import { Institution } from '../entities/institution.js';
import { type GlobalMailTemplate, InstitutionMailTemplate, MailTemplateVars } from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { Product } from '../entities/products.js';
import { UserStammdaten } from '../entities/stammdaten.js';
import { Suspension } from '../entities/suspension.js';
import { User } from '../entities/user.js';
import { ParticipationState } from '../enums/enums.js';
import { Permission, Role } from '../enums/roles.js';
import { SuspensionLog } from '../logs/SuspensionLog.js';
import { assembleGlobalMail, assembleInstitutionMail } from '../mails/assemble-mail.js';
import { mailer } from '../mails/communication.js';
import { notifyEmailChangeConfirmation } from '../notification/change-email.js';
import { notifyNewsletterOptIn } from '../notification/newsletter-opt-in.js';
import { notifyNuzterSperrungAufgehoben } from '../notification/nutzer-sperrung-aufgehoben.js';
import { newsletterOptInSecretIsValid } from '../server/logic/newsletter.js';
import { createChangeEmailAction, emailChangeTicketCollectionName, type EmailChangeTicket } from '../server/logic/user.js';
import { mongo } from '../server/mongo.js';
import { notifyUserPermissionChange } from '../server/user-change-notifier.js';
import { searchMatch, withIdTiebreaker } from '../util.js';
import { getEinzelberatungByBeraterID } from './einzelberatung.js';
import { suspendParticipationsByUserInstitution } from './participation.js';
import { getInstitutionByID } from './institution.js';

export const sendFreeFormMail = registerSubscription({
  operationName: 'sendFreeFormMail',
  middlewares: [checkAuthorizedMiddleware(({ user }) => user.isSuperAdmin)],
  resolve: (mailAddresses: string[], mailTemplate: GlobalMailTemplate | InstitutionMailTemplate) => () => {
    return from(
      new Promise<Array<EntityData<User>>>(async resolve => {
        if (mailTemplate instanceof InstitutionMailTemplate && mailTemplate.institutionID) {
          const institution = await getInstitutionByID(mailTemplate.institutionID)(Institution.allFields);
          mailTemplate.institution = institution as Institution;
        }

        const users = await (
          await scopedCollection<User>(User._class)
        )
          .find({ email: { $in: mailAddresses } })
          .toArray();
        if (users.length !== Array.from(new Set(mailAddresses)).length)
          throw new Error(
            `Couldnt find users to some mail addresses (mailAddressesLength: ${mailAddresses.length}; usersLength: ${
              users.length
            }; ${mailAddresses.filter(mail => !users.map(x => x.email).includes(mail))})`,
          );

        return resolve(users);
      }),
    ).pipe(
      concatMap(users => {
        const tasks = users.map((user, i) => ({ id: i.toString(), user }));
        return merge(...tasks.map((t, i) => of(t)));
      }),
      concatMap(async ({ id, user }) => {
        const token = await generateLoginToken({
          userID: user._id,
          userClass: User._class,
          ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
        });
        const vars = {
          [MailTemplateVars.FIRST_NAME]: user.firstName,
          [MailTemplateVars.LAST_NAME]: user.lastName,
          [MailTemplateVars.TITLE]: user.title,
          [MailTemplateVars.INSTITUTION_NAME]: (mailTemplate as InstitutionMailTemplate).institution?.name,
          [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
        };

        const { subject, content: html } =
          mailTemplate instanceof InstitutionMailTemplate
            ? await assembleInstitutionMail({ template: mailTemplate, infos: vars })
            : await assembleGlobalMail({ template: mailTemplate, infos: vars });

        if (!subject) throw new Error('Bitte tragen Sie einen Betreff ein');
        if (!html) throw new Error('Bitte tragen Sie den Inhalt ein');

        await (
          await mailer
        ).sendMail(await prepareMessage({ html, subject, attachments: [] }), new Recipient(user.email));

        // dont bulk send all emails, especially for larger amounts
        await delay(100);

        return id;
      }),
    );
  },
  type: () => String,
  params: [
    { name: 'mailAddresses', type: () => [String] },
    { name: 'mailTemplate', type: () => MailTemplate },
  ],
});

export const subscribeParticipantsByInstitutionPaginated = registerSubscription({
  operationName: 'subscribeParticipantsByInstitutionPaginated',
  type: () => [User],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve: (skip: number, limit: number, filter?: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<User>) => {
      skip ??= 0;
      limit ??= 9999999;

      const institutionID = filter?.institutionID;
      if (!institutionID) throw new Error('Missing institutionID');

      return watchEntireCollection(`${User._collectionName}`).pipe(
        switchMap(async () => {
          // TODO: use auth middleware
          const user = await getCurrentUserServer();
          if (
            !context.serverContext &&
            !user?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, institutionID)
          ) {
            throw new Error('not authorized');
          }

          const participationFilter = { 'product.institutionID': institutionID } as any;

          if (filter.timeframeID) {
            participationFilter['product.timeframeID'] = filter.timeframeID;
          }

          const aggregation = [
            { $match: participationFilter },
            { $group: { _id: '$userID', user: { $first: '$user' }, stammdaten: { $first: '$userStammdaten' } } },
            { $replaceRoot: { newRoot: { $mergeObjects: ['$user', { stammdaten: '$stammdaten' }] } } },
            { $match: searchMatch(['firstName', 'lastName', 'email'], filter.search) },
            { $sort: withIdTiebreaker(filter?.getMongoOptions().sort) },
            { $project: gqlFields },
            { $skip: skip },
            { $limit: limit },
          ];

          const participationCollection = mongo().collection(`${Participation._collectionName}-materialized`);
          return (await participationCollection.aggregate(aggregation).toArray()) as User[];
        }),
      );
    };
  },
});

export const createUserForInstitution = registerMutation({
  operationName: 'createUserForInstitution',
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) => {
      if (user.isSuperAdmin) return true;
      if (!params.institutionID) return false;
      if (params.type === 'user') return user.hasPermission(Permission.MANAGE_PARTICIPATION, params.institutionID);
      if (params.type === 'lecturer') return user.hasPermission(Permission.EDIT_LECTURERS, params.institutionID);
      if (params.type === 'referent') return user.hasPermission(Permission.EDIT_LECTURERS, params.institutionID);
      return false;
    }),
  ],
  params: [
    { name: 'institutionID', type: () => String },
    { name: 'user', type: () => User },
    { name: 'type', type: () => String },
    { name: 'stammdaten', type: () => UserStammdaten },
  ],
  type: () => User,
  resolve: (institutionID: string, user: User, type: 'user' | 'lecturer' | 'referent', stammdaten?: UserStammdaten) => {
    return async (gqlFields: BaseQLSelectionSet<User>) => {
      if (!institutionID) throw new Error('Missing institutionID');
      const userCollection = mongo().collection(User._collectionName);
      const stammdatenCollection = mongo().collection(UserStammdaten._collectionName);

      const userID = await (async () => {
        if (user._id) {
          const existingUser = await loadRawEntityDataByID(User, user._id);
          if (existingUser) return existingUser._id;
        }

        // NOTE: this can happen when the user already exists but only participated in workshops of other institutions.
        //       In this case the admin cant know that the user already exists
        const mailRegex = { $regex: `^${user.email.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' };
        const existingUserByMail = await userCollection.findOne({ email: mailRegex });
        if (existingUserByMail) {
          user._id = existingUserByMail._id.toString();
          return existingUserByMail._id.toString();
        }

        // Only profile fields may come from the caller; roles are assigned below.
        user = new User({
          _class: User._class,
          email: user.email,
          username: user.username,
          firstName: user.firstName,
          lastName: user.lastName,
          title: user.title,
          dateOfBirth: user.dateOfBirth,
        });
        return await user.create();
      })();

      await runInContext(serverContext.sessionID, async () => {
        // add role with institution context, so that publish filter do not prevent admins from seeing the user
        if (type === 'user') {
          await ensureSelfRoles(userID);
          await assignRoleToUser(userID, Role.PARTICIPANT, [institutionID], User as any);
        }

        if (type === 'lecturer') await assignRoleToUser(userID, Role.LECTURER, [institutionID], User as any);
        if (type == 'referent') await assignRoleToUser(userID, Role.REFERENT, [institutionID], User as any);
        notifyUserPermissionChange({ userID, kind: 'roles' });

        if (stammdaten) {
          stammdaten.userID = userID; // userID might have changed (e.g. existing user found by email)
          stammdaten.institutionID = institutionID;
          const existingStammdaten = await stammdatenCollection.findOne({ userID, institutionID });
          if (existingStammdaten) {
            // in case stammdaten already exist just update them
            stammdaten._id = existingStammdaten._id.toString();
            await stammdaten.save();
          } else {
            const stammdatenPlugin = getDbPluginForScope('registration::stammdaten', institutionID);
            const newStammdaten = await stammdatenPlugin.createStammdaten({ userID, institutionID, createInDB: false });
            for (const [key, value] of Object.entries(stammdaten.toJSON())) newStammdaten[key] = value;
            await newStammdaten.create();
          }
        }
      });

      user._id = userID;
      return user;
    };
  },
});

async function assertCanRequestUserMail(userID: string): Promise<void> {
  const caller = await getCurrentUserServerOrFail();
  const impersonatingUser = (context as typeof context & { impersonatingUser?: string }).impersonatingUser;
  if (
    (caller._id === userID && !impersonatingUser) ||
    caller.isSuperAdmin ||
    caller.hasPermission(Permission.MANAGE_USER, GLOBAL_CONTEXT)
  )
    return;
  throw new NotAuthorizedError(Permission.MANAGE_USER, GLOBAL_CONTEXT);
}

function emailMatch(email: string) {
  const re = { $regex: `^${email.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' };
  return { $or: [{ email: re }, { username: re }] };
}

export const sendUserNewsletterOptInMail = registerMutation({
  operationName: 'sendUserNewsletterOptInMail',
  params: [{ name: 'userID', type: () => String }],
  type: () => Int,
  resolve: (userID: string) => {
    return async () => {
      await assertCanRequestUserMail(userID);
      await notifyNewsletterOptIn(userID);

      return 1;
    };
  },
});

export const handleNesletterOptInReuest = registerMutation({
  operationName: 'handleNesletterOptInReuest',
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => String },
    { name: 'token', type: () => String },
  ],
  resolve: (userID: string, token: string) => async () => {
    if (!newsletterOptInSecretIsValid(userID, token)) throw new ExpectedError('Der Link ist ungültig.');

    const userCollection = mongo().collection(User._collectionName);
    await userCollection.updateOne({ _id: userID as any }, { $set: { receivesNewsletter: true } });

    return true;
  },
});

export const setNewsletterOptIn = registerMutation({
  operationName: 'setNewsletterOptIn',
  type: () => Boolean,
  resolve: () => async () => {
    const user = await getCurrentUserServerOrFail();

    const userCollection = mongo().collection(User._collectionName);
    await userCollection.updateOne({ _id: user._id as any }, { $set: { receivesNewsletter: true } });

    return true;
  },
});

export const disconnectZoomAccount = registerMutation({
  operationName: 'disconnectZoomAccount',
  type: () => Boolean,
  resolve: () => async () => {
    const user = await getCurrentUserServerOrFail();

    const userCollection = mongo().collection<User>(User._collectionName);
    const existing = await userCollection.findOne({ _id: user._id as any }, { projection: { zoomAccessTokenID: 1 } });
    const tokenID = existing?.zoomAccessTokenID;

    await userCollection.updateOne({ _id: user._id as any }, { $set: { zoomAccessTokenID: null } });

    if (tokenID) {
      const tokenCollection = mongo().collection<ZoomUserAccessToken>(ZoomUserAccessToken._collectionName);
      await tokenCollection.deleteOne({ _id: tokenID });
    }

    return true;
  },
});

export const handleChangeEmailRequest = registerMutation({
  operationName: 'handleChangeEmailRequest',
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => String },
    { name: 'token', type: () => String },
  ],
  resolve: (userID: string, token: string) => async () => {
    const invalidLink = () => new ExpectedError('Der Link ist ungültig oder abgelaufen.');
    if (typeof token !== 'string' || !/^[0-9a-f]{64}$/.test(token)) throw invalidLink();
    const ticketCollection = mongo().collection<EmailChangeTicket>(emailChangeTicketCollectionName);
    const ticket = await ticketCollection.findOne({
      _id: createHash('sha256').update(token).digest('hex'),
      userID,
      expiresAt: { $gt: new Date() },
    });
    if (!ticket) throw invalidLink();

    const userCollection = mongo().collection<User>(User._collectionName);
    const existingUser = await userCollection.findOne({ _id: { $ne: userID }, ...emailMatch(ticket.newEmail) });
    if (existingUser) throw new ExpectedError('Ein Nutzer mit dieser E-Mail existiert bereits.');

    const consumedTicket = await ticketCollection.findOneAndDelete({
      _id: ticket._id,
      userID,
      expiresAt: { $gt: new Date() },
    });
    if (!consumedTicket) throw invalidLink();

    const user = await userCollection.findOne({ _id: userID }, { projection: { email: 1, username: 1 } });
    if (!user) throw invalidLink();
    const updateUsername =
      typeof user.username === 'string' &&
      typeof user.email === 'string' &&
      user.username.toLowerCase() === user.email.toLowerCase();
    const updateResult = await userCollection.updateOne(
      { _id: userID },
      {
        $set: {
          email: consumedTicket.newEmail,
          emailConfirmed: true,
          ...(updateUsername ? { username: consumedTicket.newEmail } : {}),
        },
      },
    );
    if (updateResult.matchedCount !== 1) throw invalidLink();

    return true;
  },
});

export const requestChangeEmail = registerMutation({
  operationName: 'requestChangeEmail',
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => String },
    { name: 'newEmail', type: () => String },
  ],
  resolve: (userID: string, newEmail: string) => async () => {
    await assertCanRequestUserMail(userID);
    if (typeof newEmail !== 'string') throw new ExpectedError('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
    newEmail = newEmail.trim().toLowerCase();
    if (newEmail.length < 6 || !emailRegexCheck(newEmail))
      throw new ExpectedError('Bitte geben Sie eine gültige E-Mail-Adresse ein.');

    const userCollection = mongo().collection<User>(User._collectionName);
    const existingUser = await userCollection.findOne({ _id: { $ne: userID }, ...emailMatch(newEmail) });
    if (existingUser) throw new ExpectedError('Ein Nutzer mit dieser E-Mail existiert bereits.');

    const token = await createChangeEmailAction(userID, newEmail);
    await notifyEmailChangeConfirmation(userID, newEmail, token);

    return true;
  },
});

export async function ensureSelfRoles(userID: string) {
  const user = await getUserByID(userID)({ _id: 1, roles: selectionSet(() => AdornisRoleToContextsWrapper, 2) });

  if (!user) return;

  // We want to ensure that everyone has a User role on it's own context.
  // Otherwise you can't change your own Password.
  if (!user.hasPermission(Permission.ChangePassword, user._id)) {
    user.roles.push(new AdornisRoleToContextsWrapper({ name: Role.USER, contexts: [user._id] }));

    // raw updateOne bypasses `User.afterUpdate` — invalidate the publishFilter cache ourselves.
    await (
      await scopedCollection<User>(User._class)
    ).updateOne(
      { _id: user._id },
      { $set: { roles: user.roles.map(r => r.toJSON() as EntityData<AdornisRoleToContextsWrapper>) } },
    );
    notifyUserPermissionChange({ userID: user._id, kind: 'roles' });
  }
}

export async function ensureDynamicRoles(userID: string) {
  const user = await getUserByID(userID)({
    _id: 1,
    roles: selectionSet(() => AdornisRoleToContextsWrapper, 2),
    suspentions: { institutionID: 1 },
  });

  if (!user) return;

  // remove all existing dynamic roles
  user.roles = user.roles.filter(role => !(role instanceof DynamicAdornisRoleToContextsWrapper));

  // add new dynamic roles
  const dynamicRoles: DynamicAdornisRoleToContextsWrapper[] = [];

  const beraterRoles = await getEinzelberatungByBeraterID(userID)(selectionSet(() => Einzelberatung, 2));
  const beratungenIDs = beraterRoles.map(beratung => beratung._id);
  const suspensionIDs = user.suspentions.map(suspension => suspension.institutionID);
  const institutionIDs = beraterRoles.map(beratung => beratung.institutionID).filter(id => !suspensionIDs.includes(id));

  if (beratungenIDs.length > 0) {
    dynamicRoles.push(
      new DynamicAdornisRoleToContextsWrapper({ name: Role.BERATER, contexts: [...beratungenIDs, ...institutionIDs] }),
    );
  }

  // raw updateOne bypasses `User.afterUpdate` — invalidate the publishFilter cache ourselves.
  await (
    await scopedCollection<User>(User._class)
  ).updateOne(
    { _id: user._id },
    {
      $set: {
        roles: [...user.roles, ...dynamicRoles].map(r => r.toJSON() as EntityData<AdornisRoleToContextsWrapper>),
      },
    },
  );
  notifyUserPermissionChange({ userID: user._id, kind: 'roles' });
}

export const getAllUsersWithoutParticipation = registerQuery({
  operationName: 'getAllUsersWithoutParticipation',
  type: () => [User],
  params: [
    { name: 'workshopID', type: () => String },
    { name: 'searchString', type: () => String },
    { name: 'limit', type: () => Int },
  ],
  resolve: (workshopID: string, searchString: string, limit: number) => {
    return async (gqlFields: BaseQLSelectionSet<User>) => {
      const user = await getCurrentUserServer();
      if (!user) throw new Error('not authenticated');
      if (!user.isAdmin && !user.isSuperAdmin) throw new Error('not authorized');
      // Institution admins may add any platform user to their workshops (e.g. a participant from
      // another institution) without that user having to self-register. We therefore no longer
      // restrict candidates to users who already participated in this admin's institution.
      const locationFilter: any[] = [];

      const collection = mongo().collection(User._collectionName);
      const aggregation = [
        {
          $match: {
            $or: [
              { email: { $regex: searchString, $options: 'i' } },
              { firstName: { $regex: searchString, $options: 'i' } },
              { lastName: { $regex: searchString, $options: 'i' } },
            ],
          },
        },
        { $lookup: { from: 'participations', localField: '_id', foreignField: 'userID', as: 'participations' } },
        { $match: { $expr: { $not: { $in: [workshopID, '$participations.productID'] } } } },
        ...locationFilter,
        { $limit: limit },
      ];

      return collection.aggregate(aggregation, { collation: { locale: 'de' } }).toArray() as Promise<User[]>;
    };
  },
});

export const searchUsers = registerQuery({
  operationName: 'searchUsers',
  type: () => [User],
  params: [
    { name: 'searchString', type: () => String },
    { name: 'limit', type: () => Int },
  ],
  resolve: (searchString: string, limit: number) => {
    return async (gqlFields: BaseQLSelectionSet<User>) => {
      const user = await getCurrentUserServer();
      if (!user) throw new Error('not authenticated');
      if (!user.isSuperAdmin) throw new Error('not authorized');

      const regex = { $regex: searchString.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), $options: 'i' };
      const collection = mongo().collection<User>(User._collectionName);
      return collection
        .find({ $or: [{ email: regex }, { firstName: regex }, { lastName: regex }] }, { collation: { locale: 'de' } })
        .limit(limit)
        .toArray() as Promise<User[]>;
    };
  },
});

export async function cancelSuspensionFromInstitutionServer(userID: string, institutionID: string) {
  const user = await loadRawEntityDataByID<typeof User, { _id: 1; _class: 1; suspentions: { institutionID: 1 } }>(
    User,
    userID,
  );

  if (!user) throw new Error('User not found');

  // remove suspension
  const suspentionIndex = user.suspentions.findIndex(suspention => suspention.institutionID === institutionID);
  const removedSuspension = user.suspentions.splice(suspentionIndex, 1);

  if (removedSuspension.length === 0) throw new Error('Suspension not found');

  await saveEntityData(user);
  notifyUserPermissionChange({ userID, kind: 'suspensions' });

  // await cancelSuspensionParticipationsByUserInstitution(userID, institutionID);

  logger.info({ userID, institutionID, removedSuspension }, 'cancelSuspensionFromInstitution');
  const eventLog = new SuspensionLog({ locked: false, suspension: new Suspension(removedSuspension[0]!), userID });
  await eventLog.create();

  // update participation stati
  const _suspendedParticipations = await loadRawEntityDataByQuery(Participation, {
    userID,
    state: ParticipationState.suspended,
  });

  // only cancel from institution
  const products = await loadRawEntityDataByIDs(
    Product,
    _suspendedParticipations.map(p => p.productID),
  );
  const suspendedParticipations = _suspendedParticipations.filter(p => {
    const product = products.find(prod => prod._id === p.productID);
    return product?.institutionID === institutionID;
  });

  logger.info({ suspendedParticipations }, 'cancelSuspensionFromInstitution');
  for (const participation of suspendedParticipations) {
    participation.state = ParticipationState.left;
    await saveEntityData(participation);
  }

  if (suspendedParticipations.length > 0) {
    await notifyNuzterSperrungAufgehoben(suspendedParticipations.map(p => p._id));
  }
}

export const cancelSuspensionFromInstitution = registerMutation({
  operationName: 'cancelSuspensionFromInstitution',
  type: () => String,
  params: [
    { name: 'userID', type: () => String },
    { name: 'institutionID', type: () => String },
  ],
  resolve: (userID, institutionID) => {
    return async () => {
      const requestingUser = await getCurrentUserServerOrFail();
      if (!requestingUser.hasPermission(Permission.MANAGE_USER, institutionID))
        throw new Error('You dont have enough permission!');

      return cancelSuspensionFromInstitutionServer(userID, institutionID);
    };
  },
});

export const suspendUserFromInstitution = registerMutation({
  operationName: 'suspendUserFromInstitution',
  type: () => String,
  params: [
    { name: 'userID', type: () => String },
    { name: 'suspension', type: () => Suspension },
    { name: 'forceSuspendParticipations', type: () => [String] },
  ],
  resolve: (userID: string, suspension: Suspension, forceSuspendParticipations: string[]) => {
    return async () => {
      const institutionID = suspension.institutionID;
      if (!institutionID) throw new Error('institutionID is required');

      const activeUser = await getCurrentUserServer();
      if (!activeUser?.hasPermission(Permission.MANAGE_PARTICIPATION, institutionID)) throw new Error('no permission');

      const user = await loadRawEntityDataByIDOrFail(User, userID, {
        _id: 1,
        _class: 1,
        suspentions: { institutionID: 1 },
      });
      if (!user) throw new Error('User not found');

      if (user.suspentions.map(suspention => suspention.institutionID).includes(suspension.institutionID))
        throw new Error('Dieser Nutzer ist bereits suspendiert!');

      await mongoUpdateOne(User, { _id: userID }, { $push: { suspentions: suspension.toJSON() } });
      notifyUserPermissionChange({ userID, kind: 'suspensions' });

      await suspendParticipationsByUserInstitution(userID, suspension, forceSuspendParticipations);

      const eventLog = new SuspensionLog({ locked: true, suspension, userID });
      await eventLog.create();
    };
  },
});

export const useAction = registerMutation({
  operationName: 'useAction',
  type: () => Boolean,
  params: [{ name: 'action', type: () => ActionTokenData }],
  resolve: action => {
    return async () => {
      const actionCollection = mongo().collection<ActionTokenData>(ActionTokenData._collectionName);
      const storedAction = await actionCollection.findOne({ _id: action._id });
      if (!storedAction?.isValid) throw new Error('action is not valid');

      const consumeResult = await actionCollection.updateOne(
        { _id: storedAction._id, isValid: true },
        { $set: { isValid: false } },
      );
      if (consumeResult.modifiedCount !== 1) throw new Error('action is not valid');

      switch (storedAction.action) {
        case 'confirm-mail': {
          const userCollection = mongo().collection<User>(User._collectionName);
          const updateResult = await userCollection.updateOne(
            { _id: storedAction.userID },
            { $set: { emailConfirmed: true } },
          );
          if (updateResult.matchedCount !== 1) throw new Error('User not found');
          break;
        }
      }

      return true;
    };
  },
});

export const getUserByEmail = registerQuery({
  operationName: 'getUserByEmail',
  type: () => User,
  params: [{ name: 'email', type: () => String }],
  middlewares: [
    checkAuthorizedMiddleware(({ user }) =>
      user.roles.some(role => [Role.ADMIN, Role.SUPER_ADMIN, 'mw:admin', 'ADMIN'].includes(role.name)),
    ),
  ],
  resolve: email => {
    return async (gqlFields: BaseQLSelectionSet<User>) => {
      const collection = await scopedCollection<User>(User._class);
      const result = await collection.findOne<User>({
        email: { $regex: `^${email.trim().replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' },
      });
      return result;
    };
  },
});

export const getUserByEmailExists = registerQuery({
  operationName: 'getUserByEmailExists',
  type: () => Boolean,
  params: [{ name: 'email', type: () => String }],
  resolve: email => {
    return async () => {
      const userCol = mongo().collection<EntityData<User>>(User._collectionName);
      const existingUserByEmail = await userCol.findOne(emailMatch(email.trim()));
      return !!existingUserByEmail;
    };
  },
});

export const subscribeAdmins = registerSubscription({
  operationName: 'subscribeAdmins',
  type: () => [User],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  middlewares: [checkAuthorizedMiddleware(({ user }) => user.isSuperAdmin)],
  resolve: (skip, limit, filter) => {
    return (gqlFields: BaseQLSelectionSet<User>) => {
      const filterObject = filter.toObject();
      if (filterObject.search) filterObject.search = filterObject.search.replace(/([()[{*+.$^\\|?])/g, '\\$1');

      const userFilter = { 'roles.name': { $in: [Role.ADMIN, Role.SUPER_ADMIN] } };

      const sorting = {};
      filter.sortingFields.forEach((sortingField, index) => {
        sorting[sortingField] = filter.sortingDirections[index];
      });

      return subscribePaginatedAggregation(User, skip, limit, userFilter, [
        {
          $lookup: {
            from: Institution._collectionName,
            as: 'institutions',
            localField: 'roles.contexts',
            foreignField: '_id',
          },
        },
        {
          $addFields: {
            institutionNames: {
              $reduce: { input: '$institutions', initialValue: '', in: { $concat: ['$$value', '$$this.name'] } },
            },
          },
        },
        ...(filterObject.search
          ? [
              {
                $match: {
                  $or: [
                    { username: { $regex: filterObject.search, $options: 'i' } },
                    { firstName: { $regex: filterObject.search, $options: 'i' } },
                    { lastName: { $regex: filterObject.search, $options: 'i' } },
                    { email: { $regex: filterObject.search, $options: 'i' } },
                    { institutionNames: { $regex: filterObject.search, $options: 'i' } },
                  ],
                },
              },
            ]
          : []),
      ]);
    };
  },
});

export const subscribeUsersPaginated = registerSubscription({
  operationName: 'subscribeUsersPaginated',
  type: () => [User],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user }) =>
      user.roles.some(role => [Role.ADMIN, Role.SUPER_ADMIN, 'mw:admin', 'ADMIN'].includes(role.name)),
    ),
  ],
  resolve: (skip, limit, filter) => {
    return (gqlFields: BaseQLSelectionSet<User>) => {
      const options = filter.getMongoOptions();
      return subscribePaginated(User, skip, limit, filter.getMongoFilter(), {
        ...options,
        sort: withIdTiebreaker(options.sort),
      });
    };
  },
});

export const updateUserRoles = registerMutation({
  operationName: 'updateUserRoles',
  type: () => String,
  params: [
    { name: 'userID', type: () => String },
    { name: 'isGlobalAdmin', type: () => Boolean },
    { name: 'contexts', type: () => [String] },
  ],
  resolve: (userID, isSuperAdmin, contexts) => {
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    return async (gqlFields: void) => {
      if (context.serverContext) return;
      const contextUserID = context.userID as string;
      if (!contextUserID) return;
      const user = await getCurrentUserServerOrFail();
      const hasSuperAdminPermission = !!user.roles.find(role => role.name === Role.SUPER_ADMIN);
      if (!hasSuperAdminPermission) return;

      const updateUser = await getUserByID(userID)({
        email: 1,
        firstName: 1,
        lastName: 1,
        suspentions: { _id: 1, institutionID: 1, blockedNoLimit: 1, blockedTill: 1 },
        roles: selectionSet(() => AdornisRoleToContextsWrapper, 2),
        participations: {
          _id: 1,
          userID: 1,
          productID: 1,
          product: { _id: 1, name: 1, institutionID: 1, hasPrice: 1, prices: { unitAmount: 1, currency: 1 } },
          createdAt: 1,
          updatedAt: 1,
          state: 1,
          isNoShow: 1,
          signoffDate: 1,
          registrationDate: 1,
          fulfilledParticipationRequirements: 1,
          lastStateChangeWasInitiatedByAdmin: 1,
          currentPayment: { status: 1, participationIDs: 1, orderNr: { year: 1, nr: 1 }, amount: 1, discount: 1 },
          productPriceForUser: { unitAmount: 1, currency: 1 },
        },
      });

      if (!updateUser) throw new Error('user not found');

      const superAdminRoleIndex = updateUser.roles.findIndex(role => role.name === Role.SUPER_ADMIN);

      if (isSuperAdmin && superAdminRoleIndex === -1)
        updateUser.roles.push(new AdornisRoleToContextsWrapper({ name: Role.SUPER_ADMIN, contexts: [GLOBAL_CONTEXT] }));
      if (!isSuperAdmin && superAdminRoleIndex !== -1) updateUser.roles.splice(superAdminRoleIndex, 1);
      if (!isSuperAdmin) {
        const adminRoleIndex = updateUser.roles.findIndex(role => role.name === Role.ADMIN);
        if (adminRoleIndex !== -1) {
          const role = updateUser.roles.at(adminRoleIndex)!;
          role.contexts = contexts;
        } else {
          const newRole = new AdornisRoleToContextsWrapper({ name: Role.ADMIN, contexts });
          updateUser.roles.push(newRole);
        }
      }

      updateUser.participations = [];
      await updateUser.save();
    };
  },
});

export async function ensureStripeCustomerForUser(userID: string) {
  const collection = mongo().collection<User>(User._collectionName);
  const user = await collection.findOne({ _id: userID });
  if (!user) throw new Error('invalid userID');
  if (user.stripeCustomerID) return user.stripeCustomerID;
  const stripeCustomerResponse = await createStripeCustomer({
    email: user.email,
    name: user.firstName + ' ' + user.lastName,
  });
  await collection.updateOne({ _id: userID }, { $set: { stripeCustomerID: stripeCustomerResponse.id } });
  return stripeCustomerResponse.id;
}

export async function getAllowedUsers(user: ServerCachedUser) {
  // throw new Error('this function should not be used anymore becasue it is to slow');
  // check permissions
  const userIDs = [user._id];

  // ADMIN
  if (user.isAdmin) {
    const adminContexts = user.adminRole?.contexts ?? [];

    const aggregation = [
      { $unwind: '$roles' },
      { $unwind: '$roles.contexts' },
      { $match: { 'roles.contexts': { $in: adminContexts } } },
      { $project: { _id: 1 } },
    ];

    const userCollection = mongo().collection(User._collectionName);
    const result = await userCollection.raw.aggregate<{ _id: string }>(aggregation).toArray();

    userIDs.push(...result.map(res => res._id));
  }

  if (user.isLecturer) {
    const lecturerContexts = user.lecturerRole?.contexts ?? [];

    // TODO: also filter for workshop
    const aggregation = [
      { $unwind: '$roles' },
      { $unwind: '$roles.contexts' },
      { $match: { 'roles.contexts': { $in: lecturerContexts } } },
      { $group: { _id: null, ids: { $push: '$_id' } } },
    ];

    const userCollection = mongo().collection(User._collectionName);
    const result = await userCollection.aggregate(aggregation).toArray();
    userIDs.push(...(result.at(0)?.ids ?? []));
  }

  return userIDs;
}

export const getUsersByIDs = registerQuery({
  operationName: 'getUsersByIDs',
  type: () => [User],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<User>>(gqlFields: SelSet) =>
      readEntitiesByIDs(User, ids, gqlFields),
});

export const subscribeUsersByIDs = registerSubscription({
  operationName: 'subscribeUsersByIDs',
  type: () => [User],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<User>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(User, ids, gqlFields),
});

export const getAllUsers = registerQuery({
  operationName: 'getAllUsers',
  type: () => [User],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<User>>(gqlFields: SelSet) =>
      readAllEntities(User, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllUsersAll = registerSubscription({
  operationName: 'subscribeAllUsersAll',
  type: () => [User],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<User>>(gqlFields: SelSet) =>
      watchAllEntities(User, gqlFields, { skip, limit, excludeIDs }),
});

export const getUserByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<User>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getUsersByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeUserByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<User>>(gqlFields: SelSet) =>
    id ? subscribeUsersByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
